import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { projectAPI } from '@/utils/supabase/client';
import type { InternProfile } from '../types';

const UNASSIGNED = '__unassigned__';

interface Props {
  interns: InternProfile[];
  onCreated: () => void;
}

export function CreateTaskDialog({ interns, onCreated }: Props) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [estimate, setEstimate] = useState('');
  const [assignee, setAssignee] = useState<string>(UNASSIGNED);
  const [isCritical, setIsCritical] = useState(false);

  function reset() {
    setTitle('');
    setDescription('');
    setDueDate('');
    setEstimate('');
    setAssignee(UNASSIGNED);
    setIsCritical(false);
  }

  async function handleSubmit() {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      // Derive track from the chosen intern so it remains visible to them.
      const chosen = interns.find((i) => i.auth_user_id === assignee);
      await projectAPI.createInternTask({
        title: title.trim(),
        description: description.trim() || null,
        due_date: dueDate || null,
        time_estimate_min: estimate ? Number(estimate) : null,
        assignee_id: assignee === UNASSIGNED ? null : assignee,
        track: chosen?.intern_track ?? null,
        is_critical: isCritical,
      });
      toast.success('Task created');
      reset();
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create task');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="size-4" />
          New Task
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create intern task</DialogTitle>
          <DialogDescription>Assign work to an intern. They'll see it on their dashboard.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="task-title">Title</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Pull Encore Brands' Meta ads and write a brief"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="task-desc">Description</Label>
            <Textarea
              id="task-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does done look like? Include links and context."
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="task-due">Due date</Label>
              <Input id="task-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="task-est">Estimate (min)</Label>
              <Input
                id="task-est"
                type="number"
                min={0}
                value={estimate}
                onChange={(e) => setEstimate(e.target.value)}
                placeholder="60"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Assign to</Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger>
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassigned (whole track)</SelectItem>
                {interns
                  .filter((i) => i.auth_user_id)
                  .map((i) => (
                    <SelectItem key={i.id} value={i.auth_user_id as string}>
                      {i.display_name || i.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="task-critical" className="cursor-pointer">Mark as critical</Label>
              <p className="text-xs text-muted-foreground">Flags the task as high-priority for the intern.</p>
            </div>
            <Switch id="task-critical" checked={isCritical} onCheckedChange={setIsCritical} />
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={saving}>Cancel</Button>
          </DialogClose>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="size-4 animate-spin" />}
            Create task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
